import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { numberLocale } from '@/lib/language';
import { useEnforcement } from '@/hooks/useEnforcement';
import { useSubscription } from '@/hooks/useSubscription';
import { useSetBandMutation } from '@/hooks/useManageSubscription';
import { handleUpgrade, getCurrency } from '@/lib/checkout';
import {
  isNoActiveSubscription,
  isSchedulePending,
  isSpecialPlanConflict,
  isSubscriptionNotManageable,
  notManageableStatus,
} from '@/lib/manageSubscription';
import { trackConversion } from '@/lib/analytics';
import {
  PLANS,
  PLANS_BY_TIER,
  SUPPORT_EMAIL,
  addonPriceLabel,
  billingAccountKind,
  defaultBillingCycle,
  isCapTier,
  lookupForCycle,
  planDisplayName,
  type BillingCycle,
  type PlanInfo,
} from '@/lib/plans';
import { PlanCard, type PlanCardCta } from '@/components/pricing/PlanCard';
import { SpecialPlanCard } from '@/components/pricing/SpecialPlanCard';
import { BandChangeConfirmDialog } from '@/components/pricing/BandChangeConfirmDialog';
import { FeatureComparisonTable } from '@/components/pricing/FeatureComparisonTable';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/PageHeader';

/**
 * Preisseite nach Kontotyp (UX-Audit K37/K38/K48, D7):
 *   - Neukunde (free): Band buchen, Checkout.
 *   - Abonnent: Band wechseln ueber manage-subscription, Bestaetigung mit
 *     Richtung und Preisen (BandChangeConfirmDialog). Ein zweiter Checkout
 *     wuerde ein zweites Abo anlegen.
 *   - Sondertarif: Karte "Individueller Tarif" mit Kontakt, Baender nur zur Info.
 * Der aktuelle Tarif kommt aus dem EIGENEN Abo, nie aus dem Kontingent des
 * Eigentuemers, das `useEnforcement` fuer Mitglieder liefert.
 */
const Pricing: React.FC = () => {
  const { t, i18n } = useTranslation('pricing');
  const { plan, recommendedPlan, totalProducts, isMemberOfForeignMc, billedOwnerEmail } = useEnforcement();
  const { subscription, isLoading: subscriptionLoading } = useSubscription();
  const setBandMutation = useSetBandMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * CRO-Entscheidung (Dennis, 21.08.2026): bei Neuabschluessen ist die
   * Jahresabrechnung vorausgewaehlt, Bestandskunden starten auf ihrem
   * aktuellen Intervall. Abgeleitet statt in useState eingefroren: das Abo
   * loest asynchron auf, und eine bewusste Wahl am Schalter gewinnt danach.
   */
  const [pickedCycle, setPickedCycle] = useState<BillingCycle | null>(null);
  /**
   * I2: die Waehrung folgt der (asynchron aufgeloesten) App-Sprache, bis
   * jemand am EUR/USD-Schalter bewusst waehlt; `null` heisst "noch keine Wahl".
   */
  const [pickedCurrency, setPickedCurrency] = useState<'eur' | 'usd' | null>(null);
  const currency = pickedCurrency ?? getCurrency();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  /**
   * Seiten-Sperre gegen einen zweiten Kauf (Review Focus 2). PlanCard sperrt
   * nur den geklickten Knopf; ein Klick auf eine andere Karte, bevor der
   * Zustand neu gerendert ist, laeuft an `loadingPlan` vorbei. Der Ref
   * greift synchron.
   */
  const purchaseInFlight = useRef(false);
  /** Band, dessen Wechsel gerade auf Bestaetigung wartet (nur Abonnenten). */
  const [pendingBand, setPendingBand] = useState<{ plan: PlanInfo; lookupKey: string } | null>(null);

  const kind = billingAccountKind(subscription);
  const ownTier = subscription?.plan_tier ?? 'free';
  const currentBand = isCapTier(ownTier) ? PLANS_BY_TIER[ownTier] ?? null : null;
  const billingCycle = pickedCycle ?? defaultBillingCycle(subscription);
  const cta: PlanCardCta = kind === 'special' ? 'none' : kind === 'subscriber' ? 'bandChange' : 'checkout';
  // Welche Karte ist "Aktueller Tarif"? Nur ein lebendes Abo besitzt sein
  // Band. Eine Band-Zeile ohne lebendes Stripe-Abo (unpaid, incomplete, ID
  // verloren) steht im Checkout-Modus und muss genau dieses Band neu buchen
  // koennen, aktuell ist dann die Free-Karte. Ein Sondertarif hat keine Karte.
  const currentCardTier = kind === 'subscriber' ? ownTier : kind === 'free' ? 'free' : '';
  const purchaseLocked = subscriptionLoading || loadingPlan !== null;

  useEffect(() => {
    trackConversion('pricing_page_viewed', { currentPlan: plan, totalProducts });
  }, [plan, totalProducts]);

  // `?band=<tier>` kommt aus dem Kapazitaets-Dialog bzw. der 409-Umleitung
  // des Checkouts (K38). Geoeffnet wird der Wechsel nur fuer Abonnenten und
  // nur fuer echte Baender; der Parameter wird in jedem Fall entfernt
  // (Review Focus 4). Sieht das Konto hier nicht wie ein Abonnent aus, obwohl
  // ein echtes Band angefragt war (Stand der Abo-Zeile weicht von Stripe ab),
  // landet der Kunde nicht still, sondern mit Hinweis (Ruling P10). Einziger
  // Erzeuger fuer ein Nicht-Abonnenten-Konto ist die 409-Umleitung
  // ACTIVE_SUBSCRIPTION_EXISTS: Stripe hat ein Abo, die Zeile ist nur noch
  // nicht nachgezogen. Deshalb rowNotYetSynced und nicht noActive.
  const bandParam = searchParams.get('band');
  useEffect(() => {
    if (!bandParam || subscriptionLoading) return;
    const target = PLANS.find((p) => p.tier === bandParam && isCapTier(p.tier)) ?? null;
    if (target && kind === 'subscriber' && target.tier !== ownTier) {
      setPendingBand({ plan: target, lookupKey: lookupForCycle(target, billingCycle) });
    } else if (target && kind === 'free') {
      toast.info(t('bandChange.rowNotYetSynced'), { id: 'pricing-band-no-subscription' });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('band');
    setSearchParams(next, { replace: true });
  }, [bandParam, subscriptionLoading, kind, ownTier, billingCycle, searchParams, setSearchParams, t]);

  const startCheckout = async (lookupKey: string) => {
    if (purchaseInFlight.current) return;
    purchaseInFlight.current = true;
    setLoadingPlan(lookupKey);
    // handleUpgrade faengt jeden Fehler selbst (Toast und Sentry) und wirft nicht.
    const res = await handleUpgrade(lookupKey, currency);
    if (!res.redirected) {
      purchaseInFlight.current = false;
      setLoadingPlan(null);
    }
  };

  const onSelectBand = (planInfo: PlanInfo) => (lookupKey: string) => {
    if (purchaseInFlight.current || purchaseLocked) return;
    if (kind === 'special') return;
    trackConversion('pricing_plan_selected', { lookupKey });
    if (kind === 'subscriber') {
      setPendingBand({ plan: planInfo, lookupKey });
      return;
    }
    void startCheckout(lookupKey);
  };

  const confirmBandChange = async () => {
    if (!pendingBand || purchaseInFlight.current) return;
    const { plan: bandPlan, lookupKey } = pendingBand;
    setPendingBand(null);
    purchaseInFlight.current = true;
    setLoadingPlan(lookupKey);
    try {
      const result = await setBandMutation.mutateAsync(lookupKey);
      if (result.status === 'scheduled') {
        toast.success(
          t('bandChange.scheduled', {
            date: result.effective_at
              ? new Date(result.effective_at).toLocaleDateString(numberLocale(i18n.language))
              : t('bandChange.periodEnd'),
          }),
        );
      } else if (result.status === 'released') {
        // Wahl des aktuellen Bands hebt einen geplanten Wechsel auf, die
        // Antwort ist dann 'released', nicht 'updated'.
        toast.success(t('bandChange.released'));
      } else {
        toast.success(t('bandChange.updated', { plan: planDisplayName(bandPlan, i18n?.language, t) }));
      }
    } catch (error) {
      if (isNoActiveSubscription(error)) {
        // Die Abo-Zeile sagte "bezahlt", Stripe kennt keins. Kein stiller
        // Rueckfall in den Checkout mehr (K38): der Kunde entscheidet.
        toast.error(t('bandChange.noActive'), {
          action: { label: t('bandChange.noActiveCta'), onClick: () => { void startCheckout(lookupKey); } },
        });
        return;
      }
      // Die beiden uebrigen erwarteten 409er sind Zustaende, keine Defekte:
      // spezifische Meldung, KEIN Sentry (MAJOR-2).
      if (isSchedulePending(error)) {
        toast.error(t('bandChange.schedulePending'));
        return;
      }
      if (isSubscriptionNotManageable(error)) {
        toast.error(t('bandChange.notManageable', { status: notManageableStatus(error) }));
        return;
      }
      if (isSpecialPlanConflict(error)) {
        toast.error(t('checkout:errors.specialPlan', { email: SUPPORT_EMAIL }));
        return;
      }
      Sentry.captureException(error, { tags: { feature: 'billing', action: 'set-band' } });
      toast.error(t('bandChange.error'));
    } finally {
      purchaseInFlight.current = false;
      setLoadingPlan(null);
    }
  };

  const subtitle = totalProducts > 0
    // `products`, nicht `count`: `count` ist bei i18next der reservierte
    // Pluraltreiber und muss eine Zahl sein, hier steht die bereits
    // lokalisierte Zeichenkette.
    ? `${t('subtitle')} ${t('currentProducts', { products: totalProducts.toLocaleString(numberLocale(i18n.language)) })}`
    : t('subtitle');

  return (
    <div className="space-y-8">
      <PageHeader title={t('title')} subtitle={subtitle} />

      {isMemberOfForeignMc && billedOwnerEmail && (
        <div
          role="note"
          data-testid="pricing-member-note"
          className="rounded-lg border bg-muted/40 p-4 text-sm text-muted-foreground"
        >
          {t('memberNote', { email: billedOwnerEmail })}
        </div>
      )}

      {kind === 'special' && subscription && (
        <SpecialPlanCard tier={ownTier} productLimit={subscription.product_limit ?? 0} />
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-3">
          <Label htmlFor="billing-toggle" className={`text-sm ${billingCycle === 'monthly' ? 'font-semibold' : 'text-muted-foreground'}`}>
            {t('monthly')}
          </Label>
          <Switch
            id="billing-toggle"
            checked={billingCycle === 'yearly'}
            onCheckedChange={(checked) => setPickedCycle(checked ? 'yearly' : 'monthly')}
          />
          <Label htmlFor="billing-toggle" className={`text-sm ${billingCycle === 'yearly' ? 'font-semibold' : 'text-muted-foreground'}`}>
            {t('yearly')}
          </Label>
          {billingCycle === 'yearly' && (
            <Badge variant="secondary" className="text-xs">{t('save20')}</Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setPickedCurrency('eur')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              currency === 'eur'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('eur')}
          </button>
          <button
            onClick={() => setPickedCurrency('usd')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              currency === 'usd'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('usd')}
          </button>
        </div>
      </div>

      {/* Kapazitaets-Baender: Free + 9 Baender. "Aktuell" ist das eigene Band;
          ein Sondertarif hat keins, seine Karte steht oben. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {PLANS.map((planInfo) => (
          <PlanCard
            key={planInfo.tier}
            plan={planInfo}
            billingCycle={billingCycle}
            currency={currency}
            isCurrent={planInfo.tier === currentCardTier}
            isRecommended={!!recommendedPlan && planInfo.tier === recommendedPlan.tier}
            cta={cta}
            onUpgrade={onSelectBand(planInfo)}
            loading={loadingPlan === lookupForCycle(planInfo, billingCycle)}
            disabled={purchaseLocked}
          />
        ))}
      </div>

      {/* B2B-Ausschluss (Paket B Umsatzsteuer): prominenter Unternehmer-Hinweis
          ueber der Steuer-Fussnote. */}
      <div className="max-w-3xl mx-auto text-center space-y-1">
        <p className="text-sm font-medium text-foreground/80">{t('b2bNote')}</p>
        <p className="text-xs text-muted-foreground">{t('taxNote')}</p>
      </div>

      {/* Add-ons: Buchung im Abonnement-Tab der Einstellungen (A7O-14). */}
      <div className="rounded-lg border bg-card p-5 space-y-2">
        <h2 className="text-base font-semibold">{t('addons.title')}</h2>
        {/* H5-F3: der Preis folgt dem EUR/USD-Umschalter wie die Band-Karten. */}
        <p className="text-sm text-muted-foreground">
          {t('addons.mcAndLanguage', { price: addonPriceLabel(currency, i18n.language) })}
        </p>
        <p className="text-sm text-muted-foreground">{t('addons.included')}</p>
        <Link
          to="/settings?tab=subscription"
          className="inline-block text-sm font-medium text-primary underline underline-offset-2"
        >
          {t('addons.bookingLink')}
        </Link>
      </div>

      {/* Feature Comparison */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">{t('featureComparison')}</h2>
        <FeatureComparisonTable currentTier={currentCardTier} />
      </div>

      {/* Bestaetigung fuer den Bandwechsel eines Abonnenten (K36). */}
      <BandChangeConfirmDialog
        open={!!pendingBand}
        from={currentBand}
        to={pendingBand?.plan ?? null}
        cycle={billingCycle}
        fromCycle={defaultBillingCycle(subscription)}
        currency={currency}
        onConfirm={confirmBandChange}
        onCancel={() => setPendingBand(null)}
      />
    </div>
  );
};

export default Pricing;
